import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ShieldCheck, Lock, Loader2, Users, Crown, Info, Radio, Film, Sparkles, CheckCircle2, KeyRound, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { maybeShowInviteNudge } from "@/lib/inviteNudge";
import ChatThread from "@/components/ChatThread";
import { BloqTopicsRail } from "@/components/bloqs/BloqTopicsRail";
import { GatePill, TokenGatePill } from "@/components/common/TrustBadge";
import { LimitMeter } from "@/components/common/LimitMeter";
import { usePublicConfig } from "@/hooks/usePublicConfig";

// Owners of a growing, not-yet-monetized BLOQ get a subtle "Earn" nudge once the
// community grows past just the founder. Dismissible per-bloq. Tunable.
const MONETIZE_MIN_MEMBERS = 2;

import { BloqInfoDrawer } from "@/components/bloqs/BloqInfoDrawer";
import { UpcomingEventsStrip } from "@/components/bloqs/UpcomingEventsStrip";
import { BloqGateDialog } from "@/components/bloqs/BloqGateDialog";
import { MICROTX_ENABLED } from "@/config";
import { RoomStage } from "@/components/bloqs/RoomStage";
import { BloqEmoji, EarnNudge, LiveNowRow, RecapRow, MyBloqRow } from "@/components/bloqs/BloqRows";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

function CreateBloqDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", emoji: "🛡️", description: "", min_score: 0, require_verified: false });

  const createMut = useMutation({
    mutationFn: (payload) => api.createBloq(payload),
    onSuccess: (bloq) => {
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      qc.invalidateQueries({ queryKey: ["my-limits"] });
      toast.success("BLOQ created");
      onOpenChange(false);
      setForm({ name: "", emoji: "🛡️", description: "", min_score: 0, require_verified: false });
      if (bloq?.id) navigate(`/app/bloqs/${bloq.id}`);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      const blocked = shieldBlockedMessage(e);
      if (blocked) {
        toast.error(blocked);
      } else if (d?.code === "creation_limit" || d?.error === "creation_limit") {
        toast.error(d.message || "You've reached your BLOQ limit — raise your Trust Score to create more.");
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Could not create BLOQ");
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="create-bloq-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create a BLOQ</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-20">
              <Label className="text-xs text-bloq-muted">Emoji</Label>
              <Input
                value={form.emoji}
                onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))}
                className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg text-center text-xl"
                maxLength={2}
                data-testid="bloq-emoji-input"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-bloq-muted">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="DeFi Builders"
                className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="bloq-name-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What is this bloq about?"
              className="mt-1 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="bloq-description-input"
            />
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Minimum BLOQ Score to join: {form.min_score}</Label>
            <input
              type="range"
              min={0}
              max={95}
              step={5}
              value={form.min_score}
              onChange={(e) => setForm((f) => ({ ...f, min_score: Number(e.target.value) }))}
              className="mt-2 w-full accent-bloq-mint"
              data-testid="bloq-minscore-input"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-white/8 bg-bloq-s2 px-4 py-3">
            <div>
              <div className="text-sm font-medium">Require verified members</div>
              <div className="text-xs text-bloq-muted">Only verified accounts can join</div>
            </div>
            <Switch
              checked={form.require_verified}
              onCheckedChange={(v) => setForm((f) => ({ ...f, require_verified: v }))}
              data-testid="bloq-verified-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => form.name.trim() ? createMut.mutate(form) : toast.error("Name is required")}
            disabled={createMut.isPending}
            className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
            data-testid="create-bloq-submit-btn"
          >
            {createMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create BLOQ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaidBloqPreview({ bloq, priceLabel, open, onOpenChange, onJoin, joining, nativePurchases }) {
  const perks = bloq.membership?.benefits || bloq.membership?.perks;
  const list = Array.isArray(perks) && perks.length
    ? perks
    : [
        "All premium posts & member-only discussions",
        "Member-only live audio rooms & recaps",
        `Direct access to ${bloq.owner?.display_name || bloq.owner_name || "the creator"}'s inner bloq`,
        "Cancel anytime",
      ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid={`paid-preview-${bloq.id}`}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} size="lg" />
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2 font-heading">
                {bloq.name}
                <span className="inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-bloq-purple">
                  <Crown className="h-2.5 w-2.5" /> {priceLabel}
                </span>
              </DialogTitle>
              <DialogDescription className="flex items-center gap-1 text-bloq-muted">
                <Users className="h-3 w-3" /> {bloq.member_count} member{bloq.member_count === 1 ? "" : "s"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {bloq.description && <p className="text-sm text-white/80">{bloq.description}</p>}

        <div className="rounded-2xl border border-bloq-purple/20 bg-bloq-purple/[0.06] p-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-purple">
            <Sparkles className="h-3.5 w-3.5" /> What you'll unlock
          </div>
          <ul className="space-y-2" data-testid={`paid-preview-perks-${bloq.id}`}>
            {list.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bloq-mint" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          {nativePurchases ? (
            <div className="w-full rounded-full border border-white/10 py-3 text-center text-sm text-bloq-muted" data-testid={`paid-preview-native-${bloq.id}`}>
              Join this BLOQ in the mobile app
            </div>
          ) : (
            <Button
              onClick={onJoin}
              disabled={joining}
              className="w-full rounded-full bloq-gradient py-6 text-base font-bold text-bloq-bg hover:opacity-90 disabled:opacity-60"
              data-testid={`paid-preview-join-${bloq.id}`}
            >
              {joining ? <Loader2 className="h-5 w-5 animate-spin" /> : `Join · ${priceLabel}`}
            </Button>
          )}
        </DialogFooter>
        <p className="text-center text-[11px] text-bloq-muted"><Lock className="mr-1 inline h-3 w-3" />Secure checkout by Stripe · cancel anytime</p>
      </DialogContent>
    </Dialog>
  );
}

function DiscoverCard({ bloq, onJoined, onOpen }) {
  const qc = useQueryClient();
  const nativePurchases = usePublicConfig()?.native_purchases === true;
  const paid = !!bloq.membership?.enabled && Number(bloq.membership?.price_usd) > 0;
  const priceLabel = paid ? `$${Number(bloq.membership.price_usd).toFixed(2)}/mo` : null;
  const [payBusy, setPayBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState(null); // gate object from a 402 gate_unmet (token/NFT)

  const joinMut = useMutation({
    mutationFn: () => api.joinBloq(bloq.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      toast.success(`Joined ${bloq.name}`);
      maybeShowInviteNudge();
      onJoined(bloq);
    },
    onError: (e) => {
      const err = e?.response?.data;
      const d = err?.detail;
      if (MICROTX_ENABLED && e?.response?.status === 402 && err?.error === "gate_unmet") {
        setGateInfo(err.gate || {});
        return;
      }
      if (d?.code === "join_limit") {
        toast.error(d.message || `You've joined the max of ${d.max} BLOQs on your plan — upgrade to join more.`);
        return;
      }
      toast.error((typeof d === "string" && d) || d?.message || "Could not join bloq");
    },
  });

  const startPaidJoin = async () => {
    setPayBusy(true);
    try {
      const res = await api.bloqMembershipCheckout(bloq.id, `${window.location.origin}/app/bloqs`);
      if (res?.session_id) localStorage.setItem("bloq_bloq_checkout", JSON.stringify({ session_id: res.session_id, circle_id: bloq.id }));
      if (res?.url) window.location.href = res.url;
      else { toast.error("Couldn't start checkout"); setPayBusy(false); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "join_limit") toast.error(d.message || `You've joined the max of ${d.max} BLOQs on your plan — upgrade to join more.`);
      else toast.error((typeof d === "string" && d) || d?.message || "Couldn't start checkout");
      setPayBusy(false);
    }
  };
  const tokenGate = MICROTX_ENABLED ? bloq.gate?.token_gate : null;
  const isOpen = !bloq.gate?.min_score && !bloq.gate?.require_verified && !tokenGate;

  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4" data-testid={`discover-bloq-${bloq.id}`}>
      <div className="flex items-start gap-3">
        <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{bloq.name}</span>
            {paid ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-bloq-purple" data-testid={`bloq-price-pill-${bloq.id}`}>
                <Crown className="h-2.5 w-2.5" /> {priceLabel}
              </span>
            ) : isOpen ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-bloq-mint/40 bg-bloq-mint/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-bloq-mint">
                <Lock className="h-2.5 w-2.5" /> Open
              </span>
            ) : (
              <GatePill minScore={bloq.gate?.min_score} requireVerified={bloq.gate?.require_verified} />
            )}
            {tokenGate && (
              <span
                className="inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-bloq-purple"
                data-testid={`bloq-tokengate-pill-${bloq.id}`}
              >
                <KeyRound className="h-2.5 w-2.5" /> Token-gated
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-bloq-muted">
            <Users className="h-3 w-3" /> {bloq.member_count} member{bloq.member_count === 1 ? "" : "s"}
          </div>
        </div>
        {!bloq.eligible ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-bloq-s2 text-bloq-muted">
            <Lock className="h-4 w-4" />
          </span>
        ) : paid ? (
          nativePurchases ? (
            <span className="shrink-0 text-xs text-bloq-muted">In app</span>
          ) : (
            <Button
              onClick={() => setPreviewOpen(true)}
              variant="outline"
              className="shrink-0 rounded-full border-bloq-purple/60 bg-transparent font-semibold text-bloq-purple hover:bg-bloq-purple/10"
              data-testid={`join-paid-bloq-${bloq.id}`}
            >
              {`Join · ${priceLabel}`}
            </Button>
          )
        ) : (
          <Button
            onClick={() => joinMut.mutate()}
            disabled={joinMut.isPending}
            variant="outline"
            className="shrink-0 rounded-full border-bloq-mint/60 bg-transparent text-bloq-mint hover:bg-bloq-mint/10"
            data-testid={`join-bloq-${bloq.id}`}
          >
            {joinMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}
          </Button>
        )}
      </div>
      <p className="mt-3 text-sm text-bloq-muted">{bloq.description}</p>
      {tokenGate?.display && (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-bloq-purple" data-testid={`bloq-tokengate-req-${bloq.id}`}>
          <KeyRound className="h-3.5 w-3.5 shrink-0" /> Requires {tokenGate.display}
        </div>
      )}
      {!bloq.eligible && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-bloq-purple">
          <ShieldCheck className="h-3.5 w-3.5" /> {bloq.eligibility_reason}
        </div>
      )}
      {paid && (
        <PaidBloqPreview
          bloq={bloq}
          priceLabel={priceLabel}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          onJoin={startPaidJoin}
          joining={payBusy}
          nativePurchases={nativePurchases}
        />
      )}
      {MICROTX_ENABLED && gateInfo && (
        <BloqGateDialog
          open={!!gateInfo}
          onOpenChange={(o) => !o && setGateInfo(null)}
          bloq={{ id: bloq.id, name: bloq.name, gate: gateInfo }}
          onVerified={() => { setGateInfo(null); joinMut.mutate(); }}
        />
      )}
    </div>
  );
}

function BloqsList({ activeId }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["bloqs"], queryFn: api.listBloqs });
  const { data: limits } = useQuery({ queryKey: ["my-limits"], queryFn: api.myLimits });
  const bLimit = limits?.bloqs;
  const hasBloqCap = bLimit && typeof bLimit.max === "number"; // max:null = admin/unlimited
  const atBloqLimit = hasBloqCap && bLimit.owned >= bLimit.max;

  const openBloq = (bloq) => {
    if (bloq.is_member) navigate(`/app/bloqs/${bloq.id}`);
  };
  const joinRoom = (bloq) => navigate(`/app/bloqs/${bloq.id}?room=1`);
  const openRecaps = (bloq) => navigate(`/app/bloqs/${bloq.id}?info=1`);

  const liveBloqs = (data?.mine || []).filter((c) => c.active_room);
  const recapBloqs = (data?.mine || []).filter((c) => (c.recordings_count || 0) > 0);

  return (
    <div className="flex h-full min-h-0 w-full flex-col border-r border-white/8 bg-bloq-s1 md:w-[380px] md:shrink-0">
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <div>
          <h1 className="text-2xl font-bold">BLOQs</h1>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
            <ShieldCheck className="h-3 w-3" /> Trust-gated communities
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/app/discover?tab=bloqs")}
            className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/12 text-bloq-mint transition-colors hover:bg-bloq-mint/10"
            data-testid="discover-bloqs-btn"
            title="Discover"
          >
            <Compass className="h-5 w-5" />
          </button>
          <button
            onClick={() => (atBloqLimit ? null : setDialogOpen(true))}
            disabled={atBloqLimit}
            title={atBloqLimit ? "You've reached your BLOQ limit — raise your Trust Score to create more" : "Create a BLOQ"}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bloq-gradient text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
            data-testid="create-bloq-btn"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>

      {bLimit && (
        <div className="mx-5 mb-2 flex items-center gap-2 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2 text-xs text-bloq-muted" data-testid="bloq-quota">
          <Users className="h-3.5 w-3.5 text-bloq-mint" />
          {hasBloqCap ? (
            <span><b className="text-white">{bLimit.owned}</b> of <b className="text-white">{bLimit.max}</b> BLOQs created</span>
          ) : (
            <span><b className="text-white">{bLimit.owned}</b> BLOQs · unlimited</span>
          )}
          {atBloqLimit && <span className="text-bloq-purple">· raise your Trust Score for more</span>}
        </div>
      )}

      <LimitMeter slot="bloq_joins" label="BLOQs joined" unit="joins" testid="bloq-join-meter" className="mx-5 mb-2" />

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 pb-6">
        {isLoading ? (
          <div className="flex justify-center py-10 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <UpcomingEventsStrip />
            {liveBloqs.length > 0 && (
              <div data-testid="live-now-section">
                <div className="flex items-center gap-1.5 px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-destructive">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live now
                </div>
                <div className="space-y-1.5">
                  {liveBloqs.map((c) => (
                    <LiveNowRow key={c.id} bloq={c} onJoin={joinRoom} />
                  ))}
                </div>
              </div>
            )}
            {recapBloqs.length > 0 && (
              <div data-testid="recaps-section">
                <div className="flex items-center gap-1.5 px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
                  <Film className="h-3 w-3 text-bloq-purple" /> Recent recaps
                </div>
                {recapBloqs.map((c) => (
                  <RecapRow key={c.id} bloq={c} onOpen={openRecaps} />
                ))}
              </div>
            )}
            {data?.mine?.length > 0 && (
              <div>
                <div className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
                  My BLOQs
                </div>
                {[...data.mine].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map((c) => (
                  <MyBloqRow key={c.id} bloq={c} activeId={activeId} onOpen={openBloq} />
                ))}
              </div>
            )}
            {data?.discover?.length > 0 && (
              <div>
                <div className="px-2 pb-2 pt-2 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
                  Discover
                </div>
                <div className="space-y-3 px-1">
                  {data.discover.map((c) => (
                    <DiscoverCard
                      key={c.id}
                      bloq={c}
                      onJoined={(bloq) => navigate(`/app/bloqs/${bloq.id}`)}
                      onOpen={openBloq}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CreateBloqDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}

export default function BloqsPage() {
  const { bloqId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [infoOpen, setInfoOpen] = useState(false);
  const [replayTarget, setReplayTarget] = useState(null); // { id, t } from a replay system card
  const [roomOpen, setRoomOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState(null); // selected BLOQ topic (null = default/General)
  useEffect(() => { setActiveTopic(null); }, [bloqId]);
  // Mark the BLOQ read on open (mobile parity) — clears unread + refreshes badges. Best-effort.
  useEffect(() => {
    if (!bloqId) return;
    api.markBloqRead(bloqId)
      .then(() => {
        qc.invalidateQueries({ queryKey: ["bloqs"] });
        qc.invalidateQueries({ queryKey: ["badge-count"] });
      })
      .catch(() => {});
  }, [bloqId, qc]);
  const { data } = useQuery({ queryKey: ["bloqs"], queryFn: api.listBloqs });
  const { data: fullCircle } = useQuery({
    queryKey: ["bloq", bloqId],
    queryFn: () => api.getBloq(bloqId),
    enabled: !!bloqId,
  });
  // Topics (shared cache with the rail) — used to resolve a ?topic= deep-link into the active topic.
  const { data: topicsData } = useQuery({
    queryKey: ["bloq-topics", bloqId],
    queryFn: () => api.bloqTopics(bloqId),
    enabled: !!bloqId,
  });

  // Deep-link straight into a topic (e.g. tapping the hot-topic pill on the list): ?topic=<id>.
  // Consume the param once resolved so manual topic switching still works afterward.
  const topicParam = searchParams.get("topic");
  useEffect(() => {
    if (!topicParam) return;
    const t = (topicsData?.topics || []).find((x) => x.id === topicParam);
    if (t) {
      setActiveTopic(t);
      const next = new URLSearchParams(searchParams);
      next.delete("topic");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicParam, topicsData]);

  // Deep-link from the BLOQs list: ?room=1 jumps straight into the live room, ?info=1 opens recaps.
  useEffect(() => {
    if (!bloqId) return;
    if (searchParams.get("room") === "1") {
      setRoomOpen(true);
      searchParams.delete("room");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("info") === "1") {
      setInfoOpen(true);
      // cc_16/17 replay card deep-link: ?play=<recording_id>&t=<top_moment_s> seeks the replay
      const play = searchParams.get("play");
      if (play) setReplayTarget({ id: play, t: Number(searchParams.get("t")) || 0 });
      searchParams.delete("info");
      searchParams.delete("play");
      searchParams.delete("t");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloqId, searchParams]);

  // Return from a paid-bloq Stripe checkout — confirm payment, then open the bloq.
  useEffect(() => {
    const raw = localStorage.getItem("bloq_bloq_checkout");
    const urlSession = searchParams.get("session_id");
    if (!raw && !urlSession) return undefined;
    let stored = {};
    try { stored = raw ? JSON.parse(raw) : {}; } catch { stored = {}; }
    const sessionId = urlSession || stored.session_id;
    if (!sessionId) return undefined;
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      tries += 1;
      try {
        const res = await api.creatorMembershipCheckoutStatus(sessionId);
        if (res?.payment_status === "paid" || res?.session_status === "complete") {
          localStorage.removeItem("bloq_bloq_checkout");
          toast.success("You're in — membership active!");
          qc.invalidateQueries({ queryKey: ["bloqs"] });
          if (stored.circle_id) {
            qc.invalidateQueries({ queryKey: ["bloq", stored.circle_id] });
            navigate(`/app/bloqs/${stored.circle_id}`);
          }
          if (urlSession) setSearchParams({}, { replace: true });
          return;
        }
        if (res?.session_status === "expired") { localStorage.removeItem("bloq_bloq_checkout"); return; }
      } catch (e) {
        if (e?.response?.status === 404) { localStorage.removeItem("bloq_bloq_checkout"); return; }
      }
      if (!stopped && tries < 12) setTimeout(poll, 1500);
      else localStorage.removeItem("bloq_bloq_checkout");
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const all = [...(data?.mine || []), ...(data?.discover || [])];
  const active = all.find((c) => c.id === bloqId);
  const conversationId = active?.conversation_id;
  // Route messages to the selected topic's conversation (defaults to the BLOQ's main/General topic).
  const convId = activeTopic?.conversation_id || conversationId;
  const activeRoom = fullCircle?.active_room;

  // Who can pin a message = member role rank >= the bloq's pin_min_role rank.
  const role = fullCircle?.role || active?.role;
  const perms = fullCircle?.perms;
  const roleRank = { member: 0, admin: 1, owner: 2 };
  const canPinMessages = perms ? (roleRank[role] ?? 0) >= (roleRank[perms.pin_min_role] ?? 1) : false;
  const allowForward = perms?.allow_forward !== false;

  const headerActions = (
    <>
      <button
        onClick={() => setRoomOpen(true)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
        data-testid="bloq-rooms-btn"
        title="Live room"
      >
        <Radio className="h-4 w-4" />
        {activeRoom && <span className="absolute right-1.5 top-1.5 h-2 w-2 animate-pulse rounded-full bg-destructive" />}
      </button>
      <button
        onClick={() => setInfoOpen(true)}
        className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-white"
        data-testid="bloq-info-btn"
        title="BLOQ info"
      >
        <Info className="h-4 w-4" />
      </button>
    </>
  );

  const roomBanner = activeRoom ? (
    <button
      onClick={() => setRoomOpen(true)}
      className="flex w-full items-center gap-2 border-b border-destructive/20 bg-destructive/10 px-4 py-2 text-left text-sm transition-colors hover:bg-destructive/15"
      data-testid="bloq-live-banner"
    >
      <span className="flex items-center gap-1 rounded-full bg-destructive/20 px-2 py-0.5 text-[10px] font-bold uppercase text-destructive">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live
      </span>
      <span className="truncate font-medium">{activeRoom.title}</span>
      <span className="ml-auto shrink-0 text-xs text-bloq-muted">Tap to join</span>
    </button>
  ) : null;

  const memberCount = fullCircle?.member_count ?? active?.member_count ?? 0;
  const headerTokenGate = MICROTX_ENABLED ? (fullCircle?.gate?.token_gate || active?.gate?.token_gate) : null;
  const headerBadge = headerTokenGate ? <TokenGatePill gate={headerTokenGate} className="shrink-0" /> : null;
  const showEarnNudge = role === "owner" && !fullCircle?.membership?.enabled && memberCount >= MONETIZE_MIN_MEMBERS;
  const bloqBanner = (
    <>
      {roomBanner}
      {showEarnNudge && (
        <EarnNudge
          bloqId={bloqId}
          name={fullCircle?.name || active?.name || "this BLOQ"}
          memberCount={memberCount}
          onCta={() => setInfoOpen(true)}
        />
      )}
      <BloqTopicsRail bloqId={bloqId} activeTopicId={activeTopic?.id} onSelect={setActiveTopic} />
    </>
  );

  return (
    <div className="flex h-full min-h-0 w-full">
      <div className={cn("h-full min-h-0", bloqId ? "hidden md:flex" : "flex w-full")}>
        <BloqsList activeId={bloqId} />
      </div>
      <div className={cn("h-full min-h-0 min-w-0 flex-1", bloqId ? "flex" : "hidden md:flex")}>
        {bloqId && conversationId ? (
          <ChatThread
            conversationId={convId}
            onBack={() => navigate("/app/bloqs")}
            extraHeaderActions={headerActions}
            headerBadge={headerBadge}
            banner={bloqBanner}
            bloqId={bloqId}
            canEnableCipher={(fullCircle?.role || active?.role) === "owner"}
            pinnedMessage={fullCircle?.pinned}
            canPinMessages={canPinMessages}
            allowForward={allowForward}
          />
        ) : (
          <div className="hidden flex-1 items-center justify-center bg-bloq-bg md:flex">
            <div className="text-center text-bloq-muted">
              <Users className="mx-auto mb-3 h-12 w-12 opacity-40" />
              <p>Select a bloq to open its chat</p>
            </div>
          </div>
        )}
      </div>

      <BloqInfoDrawer
        bloq={fullCircle}
        open={infoOpen}
        onOpenChange={(o) => { setInfoOpen(o); if (!o) setReplayTarget(null); }}
        onOpenRoom={() => { setInfoOpen(false); setRoomOpen(true); }}
        replayTarget={replayTarget}
      />
      <RoomStage bloq={fullCircle} open={roomOpen} onOpenChange={setRoomOpen} />
    </div>
  );
}
